import { useMemo } from "react";
import {
  createTransaction,
  eq,
  useLiveQuery,
  NonEmptyArray,
  OperationType,
  PendingMutation,
  TransactionWithMutations,
} from "@tanstack/react-db";
import { SmartCardsCollection } from "@/domains/tanstack-db/core/collections";
import { TanstackBaseRepository, TanstackRepositoryArgs } from "../base/repository";
import {
  SMART_CARD_ORDER_BY_CREATED_ASC,
  SMART_CARD_ORDER_BY_SORT_ASC,
} from "@/domains/tanstack-db/common/constants";
import { smartCardRecordSchema, smartCardUpsertSchema } from "./schemas";
import { SmartCardRow, SmartCardUpsertInput } from "./schemas";
import { sortSmartCards } from "./sort-smart-cards";

export class SmartCardRepository extends TanstackBaseRepository<SmartCardsCollection> {
  constructor({ collection }: TanstackRepositoryArgs<SmartCardsCollection>) {
    super({
      collection,
    });
  }

  async upsertCard(params: SmartCardUpsertInput): Promise<void> {
    const input = smartCardUpsertSchema.parse(params);
    const existing = this.collection.get(input.id) ?? null;
    const record = this.createRecordFromInput(input, existing);

    const tx = existing
      ? this.collection.update(record.id, draft => {
          Object.assign(draft, record);
        })
      : this.collection.insert(record);
    await tx.isPersisted.promise;
  }

  async replaceCardsForSmartView(params: {
    smartViewId: string;
    rows: Array<SmartCardUpsertInput>;
  }): Promise<void> {
    const { smartViewId, rows } = params;

    const existingEntries = Array.from(this.collection.entries()).filter(
      ([, card]) => card.smart_view_id === smartViewId
    );
    const existingIds = existingEntries.map(([key]) => key);
    const existingById = new Map(existingEntries);

    const parsedRows = rows.map(row =>
      smartCardUpsertSchema.parse({
        ...row,
        smart_view_id: smartViewId,
      })
    );
    const recordsToInsert = parsedRows.map(parsed =>
      this.createRecordFromInput(parsed, existingById.get(parsed.id) ?? null)
    );
    const newIds = new Set(recordsToInsert.map(record => record.id));
    const idsToDelete = existingIds.filter(id => !newIds.has(id));

    if (existingIds.length === 0 && recordsToInsert.length === 0) {
      return;
    }

    const tx = createTransaction<SmartCardRow>({
      autoCommit: false,
      mutationFn: async ({ transaction }) => {
        await this.persistSmartCardMutations(transaction);
      },
    });

    try {
      tx.mutate(() => {
        if (idsToDelete.length > 0) {
          this.collection.delete(idsToDelete);
        }

        for (const record of recordsToInsert) {
          if (existingById.has(record.id)) {
            this.collection.update(record.id, draft => {
              Object.assign(draft, record);
            });
          } else {
            this.collection.insert(record);
          }
        }
      });
    } catch (error) {
      tx.rollback();
      throw error;
    }

    await tx.commit();
  }

  async deleteById({ cardId }: { cardId: string }): Promise<void> {
    const tx = this.collection.delete(cardId);
    await tx.isPersisted.promise;
  }

  useCardsBySmartViewId({ smartViewId }: { smartViewId: string | null }): {
    cards: SmartCardRow[];
    isLoading: boolean;
    error: string | null;
  } {
    const isEnabled = Boolean(smartViewId);

    const queryResult = useLiveQuery(
      q => {
        if (!isEnabled) return undefined;

        return q
          .from({ smartCards: this.collection })
          .where(({ smartCards }) => eq(smartCards.smart_view_id, smartViewId))
          .orderBy(({ smartCards }) => smartCards.sort_key, SMART_CARD_ORDER_BY_SORT_ASC)
          .orderBy(({ smartCards }) => smartCards.created_at, SMART_CARD_ORDER_BY_CREATED_ASC);
      },
      [smartViewId]
    );

    const cards = useMemo(() => {
      if (!isEnabled) return [];

      const rows = queryResult.data ?? [];
      return sortSmartCards(rows);
    }, [isEnabled, queryResult.data]);

    const error = isEnabled && queryResult.status === "error" ? "Failed to load cards." : null;

    return {
      cards,
      isLoading: isEnabled ? queryResult.isLoading : false,
      error,
    };
  }

  private createRecordFromInput(
    input: SmartCardUpsertInput,
    existing: SmartCardRow | null
  ): SmartCardRow {
    const nowIso = new Date().toISOString();

    return smartCardRecordSchema.parse({
      id: input.id,
      associated_space_account_id: input.associated_space_account_id,
      smart_context_id: input.smart_context_id,
      smart_view_id: input.smart_view_id,
      sort_key: input.sort_key ?? existing?.sort_key ?? null,
      added_by_space_account_id:
        input.added_by_space_account_id ?? existing?.added_by_space_account_id ?? null,
      derived_kind: input.derived_kind ?? existing?.derived_kind ?? null,
      derived_internal_label:
        input.derived_internal_label ?? existing?.derived_internal_label ?? null,
      originated_from_find_more:
        input.originated_from_find_more ?? existing?.originated_from_find_more ?? false,
      value_json: input.value_json ?? existing?.value_json ?? null,
      refreshed_at: input.refreshed_at ?? existing?.refreshed_at ?? null,
      created_at: input.created_at ?? existing?.created_at ?? nowIso,
      updated_at: input.updated_at ?? nowIso,
    });
  }

  private async persistSmartCardMutations(
    transaction: TransactionWithMutations<SmartCardRow>
  ): Promise<void> {
    const { onDelete, onUpdate, onInsert } = this.collection.config;

    const runHandler = async <TOperation extends OperationType>(
      type: TOperation,
      handler:
        | ((params: {
            transaction: TransactionWithMutations<SmartCardRow, TOperation>;
            collection: SmartCardsCollection;
          }) => Promise<unknown>)
        | undefined
    ) => {
      if (!handler) return;

      const scopedMutations = transaction.mutations.filter(
        (mutation): mutation is PendingMutation<SmartCardRow, TOperation> =>
          mutation.collection?.id === this.collection.id && mutation.type === type
      );

      if (scopedMutations.length === 0) {
        return;
      }

      const scopedTransaction = this.cloneTransactionWithMutations(transaction, scopedMutations);
      await handler({
        transaction: scopedTransaction,
        collection: this.collection,
      });
    };

    await runHandler("delete", onDelete);
    await runHandler("update", onUpdate);
    await runHandler("insert", onInsert);
  }

  private cloneTransactionWithMutations<TOperation extends OperationType>(
    transaction: TransactionWithMutations<SmartCardRow>,
    mutations: Array<PendingMutation<SmartCardRow, TOperation>>
  ): TransactionWithMutations<SmartCardRow, TOperation> {
    const scopedTransaction = Object.create(Object.getPrototypeOf(transaction));
    Object.assign(scopedTransaction, transaction, {
      mutations: mutations as NonEmptyArray<PendingMutation<SmartCardRow, TOperation>>,
    });
    return scopedTransaction;
  }
}
